import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";

export default function IntakeForm() {
  const { serviceId } = useParams();
  const { lang, t, trackConversion } = useLanguage();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    loadService();
  }, [serviceId, lang]);

  const loadService = async () => {
    try {
      const { data } = await API.get(`/services/${serviceId}?lang=${lang}`);
      setService(data);
      const initial = {};
      data.questions.forEach((q) => { initial[q.id] = ""; });
      setAnswers(initial);
    } catch (err) {
      toast.error("Error loading service");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields
    for (const q of service.questions) {
      if (q.required && !answers[q.id]) {
        toast.error(`${q.label}: ${t("intake.required")}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const { data } = await API.post("/intake", {
        service_id: serviceId,
        language: lang,
        answers,
      });
      setSubmitted(data);
      toast.success(t("intake.success"));
      trackConversion("intake", { service_id: serviceId });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error submitting intake");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async () => {
    if (!submitted) return;
    try {
      const { data } = await API.post("/payments/checkout", {
        service_id: serviceId,
        origin_url: window.location.origin,
        intake_id: submitted.intake_id,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error("Payment error: " + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="w-6 h-6 border-2 border-[#0A3FA3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen pt-20 pb-12 bg-[#F9FAFB]" data-testid="intake-success">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white border border-border p-8 text-center">
            <CheckCircle size={48} className="text-green-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">{t("intake.success")}</h2>
            <p className="text-sm text-muted-foreground mb-6">{service?.name} - &euro;{service?.price?.toLocaleString()}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button className="bg-[#0A3FA3] hover:bg-[#082F7A] rounded-sm" onClick={handlePayment} data-testid="proceed-payment-btn">
                {t("intake.proceed_payment")}
              </Button>
              <Link to="/dashboard">
                <Button variant="outline" className="rounded-sm" data-testid="back-dashboard-btn">{t("intake.back")}</Button>
              </Link>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              {lang === "nl" ? "U kunt ook een afspraak plannen via uw dashboard." : lang === "de" ? "Sie k\u00f6nnen auch einen Termin \u00fcber Ihr Dashboard planen." : lang === "fr" ? "Vous pouvez \u00e9galement planifier un rendez-vous via votre tableau de bord." : "You can also schedule a consultation via your dashboard."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 bg-[#F9FAFB]" data-testid="intake-form-page">
      <div className="max-w-2xl mx-auto px-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="intake-back-btn">
          <ArrowLeft size={14} /> {t("intake.back")}
        </button>

        <div className="bg-white border border-border p-8">
          <div className="mb-8">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A3FA3]">{service?.number}</span>
            <h1 className="text-2xl font-bold tracking-tight mt-1" data-testid="intake-service-name">{service?.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">{service?.description}</p>
            <div className="mt-3 text-sm font-bold text-[#0A3FA3]">&euro;{service?.price?.toLocaleString()} {t("services.per_project")}</div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {service?.questions?.map((q) => (
              <div key={q.id} data-testid={`question-${q.id}`}>
                <Label className="text-xs font-bold uppercase tracking-wider">
                  {q.label} {q.required && <span className="text-red-500">*</span>}
                </Label>
                {q.type === "text" && (
                  <Input
                    value={answers[q.id] || ""}
                    onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                    required={q.required}
                    className="mt-1.5 rounded-sm"
                    data-testid={`input-${q.id}`}
                  />
                )}
                {q.type === "textarea" && (
                  <Textarea
                    value={answers[q.id] || ""}
                    onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                    required={q.required}
                    rows={3}
                    className="mt-1.5 rounded-sm resize-none"
                    data-testid={`textarea-${q.id}`}
                  />
                )}
                {q.type === "select" && (
                  <Select value={answers[q.id] || ""} onValueChange={(val) => setAnswers({ ...answers, [q.id]: val })}>
                    <SelectTrigger className="mt-1.5 rounded-sm" data-testid={`select-${q.id}`}>
                      <SelectValue placeholder={t("intake.select_placeholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {q.options?.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}

            <Button type="submit" className="w-full bg-[#0A3FA3] hover:bg-[#082F7A] rounded-sm mt-8" disabled={submitting} data-testid="intake-submit-btn">
              {submitting ? t("intake.submitting") : t("intake.submit")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
